<template>
    <div class="app-viewer" ref="appViewer">
        <div class="app-frame" ref="appFrame">
            <iframe
                :src="iframeSrc"
                class="app-iframe"
                ref="appIframe"
                allow="fullscreen"
                sandbox="allow-same-origin allow-scripts allow-popups"
                @load="handleIframeLoad"
            ></iframe>
        </div>

        <!-- Full screen tile for animation -->
        <div
            v-if="showFullScreenTile"
            class="full-screen-tile"
            ref="fullScreenTile"
        >
            <img
                :src="desktopImageSrc"
                :alt="app.name"
                class="full-screen-icon"
                @error="handleImageError"
                ref="desktopImageRef"
            >
        </div>

        <ThanksForPlaying
            :show="showThanks"
            :site-name="systemInfo.siteName"
        />

        <GameIdInput
            v-if="app.game_id_type"
            ref="gameIdInputRef"
            :show="showGameIdInput"
            :prompt-text="app.game_id_type"
            :validation-state="validationState"
            :error-message="validationErrorMessage"
			:games="fetchedGames"
            @confirm="handleGameIdConfirm"
            @cancel="handleGameIdCancel"
            @timer-expired="handleGameIdTimerExpired"
            @retry-validation="resetValidation"
            @show-return-home-prompt="$emit('show-return-home-prompt')"
        />
    </div>
</template>

<script setup>
import {ref, onMounted, watch, computed, onUnmounted} from 'vue';
import gsap from 'gsap';
import {defaultIcon} from "@/apps.js";
import {showNotification} from "@/services/notification.js";
import {params, systemInfo} from "@/systemInfo.js";
import {cleanupChildAppMessageHandlers, initializeAppCommunication} from "@/services/appCommunication.js";
import GameIdInput from "@/components/GameIdInput.vue";
import ThanksForPlaying from "@/components/ThanksForPlaying.vue";
import {setActiveContext} from "@/services/keyboard.js";
import {setState} from "@/services/state.js";
import {fetchGamesForSite, validateGameId} from "@/services/helperFunctions.js";

const props = defineProps({
    app: {
        type: Object,
        required: true
    },
    appTileElement: {
        type: Object,
        default: null
    },
    showFullScreenTile: {
        type: Boolean,
        default: true
    },
	remoteId: {
		type: [String, Array],
		default: null
	},
	bypassThanks: {
		type: Boolean,
		default: false
	}
});

const emit = defineEmits(['update:showFullScreenTile', 'show-return-home-prompt', 'app-launch-failed', 'game-ended']);

const appViewer = ref(null);
const appFrame = ref(null);
const appIframe = ref(null);
const fullScreenTile = ref(null);
const desktopImageRef = ref(null);
const imageError = ref(false);
const iframeLoaded = ref(false);
const communicationInProgress = ref(false);
const communicationComplete = ref(false);
const showGameIdInput = ref(false);
const gameId = ref('');
const iframeSrc = ref('');
const showThanks = ref(false);
const validationState = ref('idle');
const validationTimeout = ref(null);
const validationErrorMessage = ref('');
const gameIdInputRef = ref(null);
const isShowingThanks = computed(() => showThanks.value);
const returnHomeTimeline = ref(null);
const gameStarted = ref(false);
const fetchedGames = ref([])
const launchWatchdogTimer = ref(null);

// ID-based launches show the iframe without waiting for a handshake, so a failed
// (offline) iframe navigation would otherwise leave a permanent white screen.
// Bail out only when the app never phoned home AND the network is down —
// apps that don't implement the handshake keep working while online.
const armLaunchWatchdog = () => {
    clearTimeout(launchWatchdogTimer.value);
    launchWatchdogTimer.value = setTimeout(() => {
        if (!communicationComplete.value && !navigator.onLine) {
            emit('app-launch-failed', 'No internet connection');
        }
    }, params.appStartTimeout * 1000);
};

// Function to get desktop image path
const getDesktopImagePath = (iconName) => {
    return `/cards/desktop/${iconName}`;
};

// Computed property for desktop image source
const desktopImageSrc = computed(() => {
    if (imageError.value) {
        return `/cards/desktop/${defaultIcon}`;
    }
    return getDesktopImagePath(props.app.icon_url);
});

const handleImageError = () => {
    if (imageError.value) return; // default card failed too — nothing left to try
    imageError.value = true;
};

const setIframeSrc = (options = {}) => {
	const {
		id = null,
		forceReload = true,
	} = options
	
	let url = props.app.spectator_url || props.app.app_url
	
	// Inject siteId if URL calls for it
	if (url.includes('site_id=')) {
		url = url.replace(/site_id=\d*/, `site_id=${systemInfo.siteId || 0}`);
	}
	
	// Append Game ID(s) if provided
	if (id !== null) {
		const resolvedId = Array.isArray(id)
			? id.join(',')
			: id.toUpperCase()
		url = url + resolvedId
	}
	
	const separator = url.includes('?') ? '&' : '?'
	url = url + separator + 't=' + Date.now();
	
	console.info('[APP URL] ', url)
	
	if (forceReload) {
		iframeSrc.value = ''
		setTimeout(() => {
			iframeSrc.value = url
		}, 10)
	} else {
		iframeSrc.value = url
	}
}

// Handle iframe load event
const handleIframeLoad = () => {
    iframeLoaded.value = true;
    console.info(`Frame loaded for ${props.app.name} at ${new Date().toISOString()}`);

    // Wait for iframe to be fully ready before initializing communication
    const initializeCommunication = () => {
        try {
            // Test if iframe is accessible
            const iframeDoc = appIframe.value.contentDocument || appIframe.value.contentWindow.document;
            if (iframeDoc.readyState === 'complete') {
                console.info(`Initializing communication with ${props.app.name}`);
                initializeAppCommunication(
                    props.app,
                    appIframe.value,
                    {communicationInProgress, communicationComplete},
                    (error) => emit('app-launch-failed', error)
                );
            } else {
                // If not ready, wait a bit more
                setTimeout(initializeCommunication, 100);
            }
        } catch (error) {
            // If iframe is not accessible yet, wait and retry
            setTimeout(initializeCommunication, 100);
        }
    };

    // Start initialization after a small delay
    setTimeout(initializeCommunication, 500);
};

// Handle game ID confirmation
const handleGameIdConfirm = async (id) => {
	validationState.value = 'validating'
	
	const validation = await validateGameId(props.app.app_url, id)
	
	if (validation.unavailable) {
		validationState.value = 'error'
		validationErrorMessage.value = 'Unable to validate ID'
	} else if (!validation.valid) {
		validationState.value = 'invalid'
		validationErrorMessage.value = ''
	} else {
		validationState.value = 'valid'
		showGameIdInput.value = false
		gameStarted.value = true
		setIframeSrc({id})
		setTimeout(continueAnimation, 100)
		armLaunchWatchdog()
	}
}

// Handle game ID cancellation
const handleGameIdCancel = () => {
    showGameIdInput.value = false;
    gameStarted.value = false;
    // emit('app-launch-failed', 'Game ID input cancelled');
    emit('show-return-home-prompt');
};

// Handle game ID timer expiration
const handleGameIdTimerExpired = () => {
    showGameIdInput.value = false;
    gameStarted.value = false;
    emit('app-launch-failed', 'Game ID timer expired');
};

const resetValidation = () => {
    validationState.value = 'idle';
    validationErrorMessage.value = '';
    if (validationTimeout.value) {
        clearTimeout(validationTimeout.value);
        validationTimeout.value = null;
    }
};

// Animate app launch
const animateAppLaunch = () => {
    if (!props.appTileElement || !fullScreenTile.value) {
        gsap.set(appFrame.value, {opacity: 1});
        return;
    }

	const notif = props.app.label + (props.app.status === 'PRD' ? '' : ` ${props.app.status}`)
    showNotification(`Launching app <strong>${notif}</strong>`)

    // Get the position and dimensions of the app tile
    const tileRect = props.appTileElement.getBoundingClientRect();
    const viewerRect = appViewer.value.getBoundingClientRect();

    // Set initial position and size of the full screen tile to match the app tile
    gsap.set(fullScreenTile.value, {
        x: tileRect.left - viewerRect.left,
        y: tileRect.top - viewerRect.top,
        width: tileRect.width,
        height: tileRect.height,
        borderRadius: 'var(--border-radius-lg)',
        opacity: 1
    });

    gsap.set(appFrame.value, {opacity: 0});

    const tl = gsap.timeline();

    // Animate the full screen tile to fill the screen
    tl.to(fullScreenTile.value, {
        x: 0,
        y: 0,
        width: '100%',
        height: '100%',
        borderRadius: 0,
        duration: 0.5,
        ease: 'power2.inOut',
    });

    // Hold the full screen image
    tl.to({}, {
        duration: 1,
        onComplete: async () => {
			// Connection may have dropped since launch was gated — the iframe can't load anything offline
			if (!navigator.onLine) {
				emit('app-launch-failed', 'No internet connection');
				return;
			}
			if (props.app.game_id_type) {
				// check if an already validated ID was provided by Buzztime Remote
				if (props.remoteId) {
					setIframeSrc({id: props.remoteId})
					gameStarted.value = true;
					setTimeout(continueAnimation, 100); // Skip to continue animation
					armLaunchWatchdog();
				} else {
					// ALL bypass — launch immediately with all game IDs
					if (props.app.game_id_type.toUpperCase() === 'ALL') {
						// Show the game ID input
						const games = await fetchGamesForSite(props.app.app_url, systemInfo.siteId)
						if (games.unavailable) {
							emit('app-launch-failed', 'Unable to fetch games for site');
							return
						}
						fetchedGames.value = games.games
						
						// For testing UI with multiple games + mix of public/local
						// const gamesPoker = [
						// 	{id: 2, name: 'buzztime trade show', is_public: true},
						// 	{id: 732569, name: 'Shea Test Table', is_public: false},
						// 	{id: 691425, name: 'tournament', is_public: true},
						// 	{id: 627913, name: "Sheaqa's Table", is_public: false},
						// 	{id: 1, name: 'Buzztime Global', is_public: true}
						// ]
						// fetchedGames.value = [...games.games, ...gamesPoker]
						
						const allIds = fetchedGames.value.map(g => g.id)
						
						// console.info('ALL IDS', allIds)
						setIframeSrc({id: allIds})
						gameStarted.value = true
						setTimeout(continueAnimation, 100)
						armLaunchWatchdog()
						return
					}
					
					showGameIdInput.value = true;
				}
			} else {
				setIframeSrc()
				
				// Check if communication is complete before proceeding
				if (communicationComplete.value) {
					// Continue with animation
					continueAnimation();
				} else {
					// Set up a watcher to continue animation when communication completes
					const unwatch = watch(communicationComplete, (newVal) => {
						if (newVal) {
							unwatch(); // Remove the watcher
							continueAnimation();
						}
					});
					
					// Set a timeout to handle case where communication never completes
					setTimeout(() => {
						if (!communicationComplete.value) {
							unwatch(); // Remove the watcher
							emit('app-launch-failed');
						}
					}, params.appStartTimeout * 1000);
				}
			}
		}
    });

    return tl;
};

// Function to continue animation after communication is complete
const continueAnimation = () => {
    if (returnHomeTimeline.value) return;

    // Fade in the app frame
    gsap.to(appFrame.value, {
        opacity: 1,
        duration: 0.5,
        // delay: 10,
        ease: 'power2.inOut',
        onComplete: () => {
            // Fade out the full screen tile
            gsap.to(fullScreenTile.value, {
                opacity: 0,
                duration: 0.5,
                // delay: 3.5,
                ease: 'power2.inOut',
            });
        }
    });
};

// Animate return to home
const animateReturnToHome = (targetAppTile) => {
    if (!targetAppTile || !appViewer.value) return false;

    // Kill any existing timeline
    if (returnHomeTimeline.value) {
        returnHomeTimeline.value.kill();
        returnHomeTimeline.value = null;
    }

    // Show the full screen tile again
    emit('update:showFullScreenTile', true);

    // Get the position and dimensions of the target app tile
    const tileRect = targetAppTile.getBoundingClientRect();
    const viewerRect = appViewer.value.getBoundingClientRect();

    // Create animation timeline
    const tl = gsap.timeline();
    returnHomeTimeline.value = tl;

    // Reset the full screen tile to full screen
    gsap.set(fullScreenTile.value, {
        x: 0,
        y: 0,
        width: '100%',
        height: '100%',
        borderRadius: 0,
    });

    // Fade in the full screen tile
    tl.to(fullScreenTile.value, {
        opacity: 1,
        duration: 0.5,
        ease: 'power2.inOut',
        onComplete: () => {
            iframeSrc.value = ''; // Kill iframe
        }
    });

    // Fade out the app frame
    tl.set(appFrame.value, {
        opacity: 0
    });

    // If this was an app with idReq, show the thanks message
	// TODO: skip thanks when REMOTE command is directly switching from one idReq app to another
    if (props.app.game_id_type && gameStarted.value && !showThanks.value && !props.bypassThanks) {
        // Show thanks message
        tl.call(() => {
            showThanks.value = true;
			window.dispatchEvent(new CustomEvent('remote-returned-home', {detail: {response: true}}));
			setState('thanks')
        });

        // Hold the full screen image for thxTimeout seconds
        tl.to({}, {
            duration: params.thxTimeout || 60,
            onComplete: () => {
                showThanks.value = false;
            }
        });
    } else {
        // For apps without idReq, just hold the screen for a moment
        tl.to({}, {
            onStart: () => {
              showThanks.value = false
            },
            duration: 3,
        });
    }

    // Animate the full screen tile back to the app tile position and size
    tl.to(fullScreenTile.value, {
        x: tileRect.left - viewerRect.left,
        y: tileRect.top - viewerRect.top,
        width: tileRect.width,
        height: tileRect.height,
        borderRadius: '15px',
        duration: 0.5,
        ease: 'power2.inOut',
    });

    // Fade out the full screen tile at the end
    tl.to(fullScreenTile.value, {
        opacity: 0,
        duration: 0.3,
        ease: 'power2.inOut',
    });

    // Wait for the animation to complete
    return new Promise(resolve => {
        tl.eventCallback('onComplete', () => {
            returnHomeTimeline.value = null;
            resolve(true);
        });
    });
};

// Handle valid ID
const handleValidId = () => {
    // showNotification('Game ID validated!', false, false);
    if (validationTimeout.value) {
        clearTimeout(validationTimeout.value);
        validationTimeout.value = null;
    }

    validationState.value = 'valid';
    showGameIdInput.value = false;
    gameStarted.value = true;

    // Continue with the app launch animation
    continueAnimation();
};

// Handle invalid ID
const handleInvalidId = () => {
    // showNotification('Invalid game ID or game has not started yet', false, true);
    if (validationTimeout.value) {
        clearTimeout(validationTimeout.value);
        validationTimeout.value = null;
    }

    validationState.value = 'invalid';
};

// Handle game ended
const handleGameEnded = () => {
    // showNotification('Game has ended', false, false);

    // Trigger return to home animation
    if (props.appTileElement) {
        animateReturnToHome(props.appTileElement).then(() => {
            emit('game-ended');
        });
    }
};

// Initialize animation when component is mounted
onMounted(() => {
    window.addEventListener('valid-id', handleValidId);
    window.addEventListener('invalid-id', handleInvalidId);
    window.addEventListener('game-ended', handleGameEnded);

    // Start the app launch animation with a small delay to ensure DOM is ready
    setTimeout(() => {
        animateAppLaunch();
    }, 50);
});

onUnmounted(() => {
    if (validationTimeout.value) {
        clearTimeout(validationTimeout.value);
    }
    clearTimeout(launchWatchdogTimer.value);
    window.removeEventListener('valid-id', handleValidId);
    window.removeEventListener('invalid-id', handleInvalidId);
    window.removeEventListener('game-ended', handleGameEnded);
    showThanks.value = false
    gameStarted.value = false;

    if (props.app) {
        cleanupChildAppMessageHandlers(props.app);
    }

    if (returnHomeTimeline.value) {
        returnHomeTimeline.value.kill();
        returnHomeTimeline.value = null;
    }
});

// Watch for changes in appTileElement to update animations
watch(() => props.appTileElement, (newValue) => {
    if (newValue && fullScreenTile.value) {
        console.log("App tile element changed, updating animation");
        // If we're already showing the app, don't re-animate
        if (appFrame.value.style.opacity === '1') return;

        // Re-run the animation with the new tile element
        animateAppLaunch();
    }
}, {deep: true});

// Expose methods to parent component
defineExpose({
    animateReturnToHome,
    isShowingThanks
});
</script>

<style scoped>
.app-viewer {
    /*position: fixed;*/
    top: 0;
    left: 0;
    width: calc(var(--cqw) * 100);
    height: calc(var(--cqh) * 100);
    z-index: 100;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    overflow: clip;
}

.app-frame {
    opacity: 0;
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    z-index: 101;
    position: relative;
}

.app-iframe {
    flex-grow: 1;
    border: none;
    width: 100%;
    height: 100%;
    background-color: white;
}

.full-screen-tile {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    z-index: 102;
    overflow: hidden;
    opacity: 0;
}

.full-screen-icon {
    width: 100%;
    height: 100%;
    object-fit: cover;
}
</style>